<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import Icon from '@/components/commons/Icon.vue'
import Badge from '@/components/commons/Badge.vue'
import Avatar from '@/components/commons/Avatar.vue'

interface NavItem {
  id: string
  label: string
  icon: string
  route?: string
  soon?: boolean
}

const NAV_PRIMARY: readonly NavItem[] = [
  { id: 'overview',  label: 'Обзор',          icon: 'home',      route: 'overview' },
  { id: 'pursuit',   label: 'Преследователи', icon: 'trophy',    route: 'pursuit' },
  { id: 'fleet',     label: 'Автопарк',       icon: 'truck',     route: 'fleet' },
  { id: 'history',       label: 'История',        icon: 'search',    route: 'history' },
  { id: 'stats',         label: 'Статистика',     icon: 'bar-chart', route: 'stats' },
  { id: 'unidentified',  label: 'Без номера',     icon: 'eye-off',   route: 'unidentified' },
  { id: 'routes',    label: 'Маршруты',       icon: 'map',       soon: true },
  { id: 'reports',   label: 'Отчёты',         icon: 'file-text', soon: true },
]

const NAV_SECONDARY: readonly NavItem[] = [
  { id: 'drivers',  label: 'Водители',     icon: 'users',     soon: true },
  { id: 'safety',   label: 'Безопасность', icon: 'shield',    soon: true },
  { id: 'settings', label: 'Настройки',    icon: 'settings',  route: 'settings' },
  { id: 'guide',    label: 'Справка',      icon: 'book-open', route: 'guide' },
]

const props = defineProps<{
  collapsed: boolean
  username: string
}>()

const emit = defineEmits<{
  logout: []
  'item-click': [string]
}>()

const router = useRouter()

const activeId = computed(() => {
  const name = router.currentRoute.value.name
  return typeof name === 'string' ? name : ''
})

function pick(item: NavItem) {
  if (item.soon) return
  if (!item.route) return
  if (!router.hasRoute(item.route)) return
  router.push({ name: item.route })
  emit('item-click', item.id)
}
</script>

<template>
  <aside class="sidebar" :data-collapsed="collapsed ? '1' : '0'">
    <div class="sidebar-brand">
      <div class="sidebar-brand-mark">
        <Icon name="shield" :size="16" />
      </div>
      <div v-if="!collapsed" class="sidebar-brand-text">
        <span class="sidebar-brand-name">Dozor</span>
        <span class="sidebar-brand-sub">УПРАВЛЕНИЕ АВТОПАРКОМ</span>
      </div>
    </div>

    <div v-if="!collapsed" class="sidebar-section-label">Главное</div>
    <button
      v-for="it in NAV_PRIMARY"
      :key="it.id"
      class="sidebar-link"
      :class="{ 'is-active': it.route === activeId, 'is-disabled': it.soon }"
      :disabled="it.soon"
      :title="it.soon ? 'Появится в следующих версиях' : it.label"
      type="button"
      @click="pick(it)"
    >
      <Icon :name="it.icon" :size="16" />
      <span v-if="!collapsed" class="nav-item-label">{{ it.label }}</span>
      <Badge v-if="!collapsed && it.soon" text="СКОРО" variant="soon" />
    </button>

    <div v-if="!collapsed" class="sidebar-section-label">Управление</div>
    <button
      v-for="it in NAV_SECONDARY"
      :key="it.id"
      class="sidebar-link"
      :class="{ 'is-active': it.route === activeId, 'is-disabled': it.soon }"
      :disabled="it.soon"
      :title="it.soon ? 'Появится в следующих версиях' : it.label"
      type="button"
      @click="pick(it)"
    >
      <Icon :name="it.icon" :size="16" />
      <span v-if="!collapsed" class="nav-item-label">{{ it.label }}</span>
      <Badge v-if="!collapsed && it.soon" text="СКОРО" variant="soon" />
    </button>

    <div class="sidebar-spacer" />

    <div class="sidebar-user">
      <Avatar :name="props.username" :size="collapsed ? 28 : 32" />
      <div v-if="!collapsed" class="sidebar-user-info">
        <span class="sidebar-user-name">{{ props.username }}</span>
        <span class="sidebar-user-role">Администратор</span>
      </div>
      <button
        v-if="!collapsed"
        type="button"
        class="sidebar-logout"
        title="Выход"
        @click="emit('logout')"
      >
        <Icon name="log-out" :size="14" />
      </button>
    </div>
  </aside>
</template>

<style scoped>
.sidebar {
  display: flex;
  flex-direction: column;
  width: 240px;
  height: 100vh;
  background: var(--surface);
  border-right: 1px solid var(--line);
  padding: var(--sp-3);
  gap: 2px;
  transition: width 180ms;
  overflow-y: auto;
  scrollbar-width: thin;
}
.sidebar[data-collapsed='1'] { width: 64px; padding: var(--sp-3) 8px; }

.sidebar-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  margin-bottom: var(--sp-3);
}
.sidebar-brand-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: var(--accent);
  color: white;
  flex-shrink: 0;
}
.sidebar-brand-text { display: flex; flex-direction: column; line-height: 1.1; }
.sidebar-brand-name { font-weight: 700; font-size: var(--fs-md); color: var(--ink); }
.sidebar-brand-sub  { font-size: 9px; color: var(--muted); letter-spacing: 0.08em; text-transform: uppercase; }

.sidebar-section-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--muted-2);
  padding: var(--sp-3) 8px 4px;
  font-weight: 600;
}

.sidebar-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: none;
  background: transparent;
  border-radius: var(--r-ctrl);
  font-size: var(--fs-13);
  color: var(--ink-2);
  cursor: pointer;
  text-align: left;
  transition: background 120ms, color 120ms;
  width: 100%;
  position: relative;
}
.sidebar-link:hover:not(.is-disabled) { background: var(--bg-2); color: var(--ink); }
.sidebar-link.is-active {
  background: var(--accent-soft);
  color: var(--accent-2);
  font-weight: 500;
}
.sidebar-link.is-disabled {
  cursor: not-allowed;
  color: var(--muted-2);
  opacity: 0.7;
}
.nav-item-label { flex: 1 1 auto; min-width: 0; }

.sidebar[data-collapsed='1'] .sidebar-link { justify-content: center; padding: 8px; }

.sidebar-spacer { flex: 1 1 auto; }

.sidebar-user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  border-top: 1px solid var(--line);
  margin-top: var(--sp-2);
}
.sidebar[data-collapsed='1'] .sidebar-user { justify-content: center; padding: 8px 0; }
.sidebar-user-info { display: flex; flex-direction: column; min-width: 0; flex: 1 1 auto; }
.sidebar-user-name { font-size: var(--fs-13); color: var(--ink); font-weight: 500; }
.sidebar-user-role { font-size: var(--fs-xs); color: var(--muted); }
.sidebar-logout {
  border: none;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
  padding: 4px;
  border-radius: var(--r-chip);
  display: inline-flex;
  align-items: center;
}
.sidebar-logout:hover { color: var(--danger); background: var(--danger-soft); }
</style>
